import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../components/Modal';

const TEMPLATES = [
  { id: 'standard', label: 'Standard Vehicle Rental', icon: '🚗', desc: 'For cars, vans, and standard vehicles' },
  { id: 'machine', label: 'Heavy Machinery Rental', icon: '🏗️', desc: 'For excavators, forklifts, and equipment' },
  { id: 'longterm', label: 'Long-Term Lease Agreement', icon: '📋', desc: 'Monthly or yearly lease contracts' },
];

const CLAUSES = {
  standard: [
    'The Renter agrees to use the vehicle only for lawful purposes.',
    'The Renter shall not sub-let or transfer the vehicle to a third party.',
    'The vehicle shall be returned in the same condition as received, except for normal wear and tear.',
    'Any damage incurred during the rental period is the responsibility of the Renter.',
    'Fuel policy: Return full. Refuelling charges apply if returned empty.',
    'Speed limit and traffic laws of Cyprus must be observed at all times.',
    'The Renter is responsible for any fines, penalties, or road tolls incurred during the rental.',
  ],
  machine: [
    'The Renter agrees to operate equipment only by certified/trained operators.',
    'Equipment must not be used beyond its rated capacity or for purposes other than intended.',
    'The Renter is liable for all damages arising from improper use or negligence.',
    'The Renter must report any mechanical faults immediately and cease operation.',
    'Equipment must be stored securely at the end of each work day.',
    'Fuel, oils, and consumables during the rental period are the Renter\'s responsibility.',
  ],
  longterm: [
    'The lease term is as specified in the agreement schedule.',
    'Monthly payments are due on the 1st of each month.',
    'Early termination requires 30 days written notice and may incur a fee.',
    'The Lessee is responsible for day-to-day maintenance (tyres, oil, etc.).',
    'Major mechanical repairs are covered by the Lessor.',
    'Insurance must be maintained by the Lessee for the duration of the lease.',
  ],
};

function blank() {
  return {
    templateId: 'standard',
    renterName: '',
    renterPassport: '',
    renterPhone: '',
    renterEmail: '',
    vehicleName: '',
    vehiclePlate: '',
    startDate: '',
    endDate: '',
    dailyRate: '',
    deposit: '',
    notes: '',
  };
}

export default function AgreementModal() {
  const { modals, closeModal, rentals, vehicles, showToast } = useApp();
  const open = !!modals['agreement'];

  const [step, setStep] = useState(1);
  const [f, setF] = useState(blank);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (open) { setStep(1); setF(blank()); setSigned(false); }
  }, [open]);

  function close() { closeModal('agreement'); }
  function upd(k, v) { setF(p => ({ ...p, [k]: v })); }

  const template = TEMPLATES.find(t => t.id === f.templateId) || TEMPLATES[0];
  const clauses = CLAUSES[f.templateId] || CLAUSES.standard;

  function handleGenerate() {
    if (!f.renterName) { alert('Renter name is required'); return; }
    if (!f.vehicleName) { alert('Vehicle is required'); return; }
    setStep(2);
  }

  function handleFinish() {
    if (!signed) { alert('Please confirm the agreement by checking the signature box'); return; }
    showToast(`Agreement generated for ${f.renterName}`);
    close();
  }

  return (
    <Modal open={open} onClose={close} size="lg">
      <ModalHeader
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>}
        title={step === 1 ? 'New Agreement' : 'Agreement Preview'}
        subtitle={step === 1 ? 'Fill in details to generate the agreement' : 'Review and confirm'}
        onClose={close}
        extra={
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2].map(s => (
              <div key={s} style={{
                width: 20, height: 20, borderRadius: '50%', fontSize: 10, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step >= s ? 'var(--ac)' : 'var(--bd)',
                color: step >= s ? '#fff' : 'var(--tx3)',
              }}>{s}</div>
            ))}
          </div>
        }
      />

      <ModalBody>
        {step === 1 && (
          <div>
            {/* Template selector */}
            <div style={{ marginBottom: 14 }}>
              <div className="fl" style={{ marginBottom: 6 }}>Agreement Template</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                {TEMPLATES.map(t => (
                  <div
                    key={t.id}
                    onClick={() => upd('templateId', t.id)}
                    style={{
                      border: `2px solid ${f.templateId === t.id ? 'var(--ac)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)', padding: '8px 10px', cursor: 'pointer',
                      background: f.templateId === t.id ? 'var(--ac-l)' : 'var(--sur)',
                    }}
                  >
                    <div style={{ fontSize: 18 }}>{t.icon}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, marginTop: 3 }}>{t.label}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>{t.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Renter Details</div>
                <div className="fg"><label className="fl">Full Name *</label>
                  <input className="fi" value={f.renterName} onChange={e => upd('renterName', e.target.value)} placeholder="Andreas Demetriou" />
                </div>
                <div className="fg"><label className="fl">Passport / ID</label>
                  <input className="fi" value={f.renterPassport} onChange={e => upd('renterPassport', e.target.value)} placeholder="K123456" />
                </div>
                <div className="fg"><label className="fl">Phone</label>
                  <input className="fi" value={f.renterPhone} onChange={e => upd('renterPhone', e.target.value)} placeholder="+357 99 000 001" />
                </div>
                <div className="fg"><label className="fl">Email</label>
                  <input className="fi" value={f.renterEmail} onChange={e => upd('renterEmail', e.target.value)} placeholder="email@example.com" />
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Vehicle & Terms</div>
                <div className="fg"><label className="fl">Vehicle Name *</label>
                  <input className="fi" value={f.vehicleName} onChange={e => upd('vehicleName', e.target.value)} placeholder="Ford Transit Van" />
                </div>
                <div className="fg"><label className="fl">Plate</label>
                  <input className="fi" value={f.vehiclePlate} onChange={e => upd('vehiclePlate', e.target.value)} placeholder="NXT987" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div className="fg"><label className="fl">Start Date</label>
                    <input className="fi" type="date" value={f.startDate} onChange={e => upd('startDate', e.target.value)} />
                  </div>
                  <div className="fg"><label className="fl">End Date</label>
                    <input className="fi" type="date" value={f.endDate} onChange={e => upd('endDate', e.target.value)} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div className="fg"><label className="fl">Daily Rate (€)</label>
                    <input className="fi" type="number" min="0" value={f.dailyRate} onChange={e => upd('dailyRate', e.target.value)} placeholder="65" />
                  </div>
                  <div className="fg"><label className="fl">Deposit (€)</label>
                    <input className="fi" type="number" min="0" value={f.deposit} onChange={e => upd('deposit', e.target.value)} placeholder="300" />
                  </div>
                </div>
              </div>
            </div>

            <div className="fg">
              <label className="fl">Additional Notes</label>
              <textarea className="fi" rows={2} style={{ resize: 'vertical' }} value={f.notes} onChange={e => upd('notes', e.target.value)} placeholder="Any special conditions or notes..." />
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            {/* Agreement preview */}
            <div style={{
              background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--r)',
              padding: 20, fontSize: 12, lineHeight: 1.7, fontFamily: 'Georgia, serif',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>VEHICLE RENTAL AGREEMENT</div>
                <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>{template.label}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12, fontSize: 11 }}>
                <div><strong>Renter:</strong> {f.renterName}</div>
                <div><strong>ID/Passport:</strong> {f.renterPassport || '—'}</div>
                <div><strong>Phone:</strong> {f.renterPhone || '—'}</div>
                <div><strong>Email:</strong> {f.renterEmail || '—'}</div>
                <div><strong>Vehicle:</strong> {f.vehicleName}</div>
                <div><strong>Plate:</strong> {f.vehiclePlate || '—'}</div>
                <div><strong>Start:</strong> {f.startDate || '—'}</div>
                <div><strong>End:</strong> {f.endDate || '—'}</div>
                <div><strong>Daily Rate:</strong> {f.dailyRate ? `€${f.dailyRate}` : '—'}</div>
                <div><strong>Deposit:</strong> {f.deposit ? `€${f.deposit}` : '—'}</div>
              </div>

              <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 10, marginBottom: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Terms & Conditions</div>
                <ol style={{ paddingLeft: 16, margin: 0 }}>
                  {clauses.map((c, i) => (
                    <li key={i} style={{ marginBottom: 4, fontSize: 11 }}>{c}</li>
                  ))}
                </ol>
              </div>

              {f.notes && (
                <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 8, fontSize: 11 }}>
                  <strong>Additional Notes:</strong> {f.notes}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20, paddingTop: 12, borderTop: '1px solid var(--bd)', fontSize: 11 }}>
                <div>
                  <div style={{ borderBottom: '1px solid var(--tx1)', marginBottom: 4, height: 28 }} />
                  <div>Renter Signature</div>
                  <div style={{ color: 'var(--tx3)' }}>{f.renterName}</div>
                </div>
                <div>
                  <div style={{ borderBottom: '1px solid var(--tx1)', marginBottom: 4, height: 28 }} />
                  <div>Company Representative</div>
                  <div style={{ color: 'var(--tx3)' }}>FleetRent CY</div>
                </div>
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer', fontSize: 12 }}>
              <input type="checkbox" checked={signed} onChange={e => setSigned(e.target.checked)} />
              I confirm this agreement has been reviewed and signed by both parties
            </label>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {step === 1 ? (
          <>
            <button className="btn btn-ghost" onClick={close}>Cancel</button>
            <button className="btn btn-primary" onClick={handleGenerate}>Generate Agreement →</button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-ghost btn-sm" onClick={() => showToast('Print dialog opened')}>
              🖨️ Print
            </button>
            <button className="btn btn-primary" onClick={handleFinish} disabled={!signed}>
              ✓ Confirm Agreement
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
